/* ===================================================================
   Pure helper utilities shared by the spine, the orchestrator,
   the corpus seeders, and the UI components.
=================================================================== */
import { SIG, DISC_CW, DISC_BY, DISC_CO, DOC_TYPE_LABEL } from "./constants";
import { docFileName, docHref } from "./data/lumenDocs";
import { attachmentFileUrl } from "./api/attachments";
import type { SimAttachment, SimEmail, ProcessedEntry } from "./types";

/* ----- token / cost estimation ----- */
export const estTok = (s: string): number =>
  Math.max(1, Math.ceil((s || "").length / 4));

/* ----- CSS variable shorthand ----- */
export const cvar = (v: string): string => `var(${v})`;

/* ----- tag color/label lookup ----- */
export function tagMeta(k: string): [string, string] {
  const map: Record<string, [string, string]> = {
    memo: ["--amber", "deal-memo candidate"],
    doc: ["--blue", "document-relevant"],
    log: ["--low", "logistics"],
    anom: ["--red", "flag"],
    multi: ["--blue", "multi-workstream"],
    priv: ["--red", "privilege"],
    noise: ["--low", "noise"],
  };
  return map[k] || ["--low", k];
}

/* ----- email-body builder used by the seed corpora ----- */
export const buildRaw = (
  text: string,
  sig: string,
  disc: string,
  quoted?: string,
): string =>
  text + "\n\n" + SIG[sig] + "\n\n" + disc + (quoted ? "\n\n" + quoted : "");

/* Convenience disclaimer re-exports so seed authors can import everything
   they need from a single place. */
export { DISC_CW, DISC_BY, DISC_CO };

/* ----- doc-type label ----- */
export const docTypeLabel = (t: string): string => DOC_TYPE_LABEL[t] || t;

/* ----- file-href / download-name helpers ----- */
export interface FileHrefable {
  fileData?: string;
  filename?: string;
  /** Backend Attachment.id — preferred source: the stored original document. */
  serverId?: string;
}
export function fileHref(o: FileHrefable | null | undefined): string {
  if (o && o.serverId) return attachmentFileUrl(o.serverId);
  return o && o.fileData ? o.fileData : docHref(o && o.filename);
}
export function fileDownloadName(o: FileHrefable | null | undefined): string {
  if (o && (o.serverId || o.fileData)) return o.filename || "";
  return docFileName(o && o.filename);
}

/* ----- doc-type sniffer for uploaded files ----- */
export function guessDocType(name: string | undefined | null): string {
  const n = String(name || "").toLowerCase();
  if (/charter|certificate of inc/.test(n)) return "charter";
  if (/term.?sheet|\bts\b/.test(n)) return "term_sheet";
  if (/spa|purchase/.test(n)) return "spa";
  if (/\bira\b|rights agreement/.test(n)) return "ira";
  if (/rofr|co.?sale/.test(n)) return "rofr";
  if (/voting/.test(n)) return "voting_agreement";
  if (/side.?letter/.test(n)) return "side_letter";
  if (/cap.?table/.test(n)) return "cap_table";
  if (/opinion/.test(n)) return "opinion_letter";
  if (/engagement/.test(n)) return "engagement_letter";
  if (/nda|non.?disclosure/.test(n)) return "nda";
  if (/disclosure/.test(n)) return "disclosure_schedule";
  if (/closing.?checklist/.test(n)) return "closing_checklist";
  if (/board|consent/.test(n)) return "board_consent";
  if (/\bmsa\b|master services/.test(n)) return "msa";
  return "other";
}

/* ----- async file → attachment (self-contained data URL) ----- */
export interface UploadedAttachment {
  filename: string;
  fileData: string;
  size: number;
}
export function readFileAsAttachment(file: File): Promise<UploadedAttachment> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () =>
      resolve({ filename: file.name, fileData: String(r.result || ""), size: file.size });
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

/* ----- generic helpers ----- */
export function cloneJSON<T>(x: T): T {
  return JSON.parse(JSON.stringify(x));
}
export function uid(): string {
  return Math.random().toString(36).slice(2, 8);
}
export function nowStr(): string {
  return new Date().toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/* ----- display date formatting — one short format for dates ("Jun 4, 2026")
   and one for date-times ("Jun 4, 2026, 4:12 PM"), applied app-wide so raw
   ISO-8601 strings never reach the UI. Unparseable input passes through. */
export function fmtDate(s: string | number | Date | null | undefined): string {
  if (s == null || s === "") return "";
  const d = s instanceof Date ? s : new Date(s);
  if (isNaN(d.getTime())) return String(s);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
export function fmtDateTime(s: string | number | Date | null | undefined): string {
  if (s == null || s === "") return "";
  const d = s instanceof Date ? s : new Date(s);
  if (isNaN(d.getTime())) return String(s);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
/* Email `time` fields conventionally read "Jun 4, 2026 · 9:14 AM"; imported
   emails may carry raw ISO timestamps instead — normalize those for display. */
export function fmtEmailTime(s: string | null | undefined): string {
  if (!s) return "";
  if (!/^\d{4}-\d{2}-\d{2}T/.test(s)) return s;
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return (
    fmtDate(d) +
    " · " +
    d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
  );
}

/* ----- per-check pass/fail/flag status ----- */
const DETECTORS_SET = new Set(["conflict", "anomaly", "newpart", "privilege", "phase"]);
export function statusOf(id: string, conf: number, tau: number): "pass" | "flag" | "fail" {
  if (DETECTORS_SET.has(id)) return conf >= tau ? "flag" : "pass";
  if (id === "catalog" && conf < 0.5) return "fail";
  return conf >= tau ? "pass" : "flag";
}

/* ----- document register accumulator ----- */
/* Filename stem: extension off, separators unified, and everything from the
   first version token onward dropped — "Helix-Series-A-SPA-v2-Wexford-redline.docx"
   and "Helix_Series_A_SPA_v3_clean.pdf" both reduce to "helix_series_a_spa",
   so successive versions of one document land in one register entry. */
function _docStem(filename: string): string {
  return (filename || "")
    .toLowerCase()
    .replace(/\.[a-z0-9]+$/, "")
    .replace(/-/g, "_")
    .replace(/_v\d+(\.\d+)?(_.*)?$/, "");
}
function _docKey(a: SimAttachment): string {
  return a.doc_type + "::" + _docStem(a.filename);
}
export { _docKey };

/* Corpus versions arrive as numbers or strings ("v3.1 (clean)", "v1 — executed"). */
export function parseDocVersion(v: unknown): number {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  const m = String(v ?? "").match(/\d+(\.\d+)?/);
  return m ? parseFloat(m[0]) : 1;
}

export interface DocRegisterVersion {
  version: number;
  filename: string;
  source_email_id: number;
  date: string;
  /** Original-file download sources, threaded from the attachment. */
  fileData?: string;
  serverId?: string;
  /** Explicit password supplied by the sender for this attachment. */
  password?: string;
  /** Authoritative currency verdict from the doc_register agent (live runs). */
  is_current?: boolean;
  supersedes?: number | null;
  authority_source?: string;
}
export interface DocRegisterEntry {
  doc_type: string;
  name: string;
  current_version: number;
  versions: DocRegisterVersion[];
  /** Set when a new version contradicts a prior lawyer assertion — the register
      defers the currency call to the lawyer rather than auto-resolving. */
  authority_conflict?: boolean;
  authority_conflict_reason?: string;
}

/* Per-attachment authoritative verdict emitted by the doc_register agent. */
interface DocRegisterVerdict {
  filename?: string;
  classification?: string;
  version?: number;
  supersedes?: number | null;
  is_current?: boolean;
  authority_source?: string;
  authority_conflict?: boolean;
  authority_conflict_reason?: string;
}
function docRegisterVerdicts(checks: ProcessedEntry["checks"]): Map<string, DocRegisterVerdict> {
  const m = new Map<string, DocRegisterVerdict>();
  const c = (checks || []).find((x) => x.id === "doc_register");
  const docs = (c && c.result && (c.result.documents as DocRegisterVerdict[])) || [];
  docs.forEach((d) => {
    if (d && d.filename) m.set(d.filename, d);
  });
  return m;
}

/* Accumulate the document register across processed emails. Latest/Old grouping
   is driven by the doc_register agent's authoritative verdict (is_current /
   supersedes / lawyer-asserted authority) when present; pre-baked seed replays
   carry no structured verdict, so they fall back to highest-version currency. */
export function accumulateDocumentRegister(
  processedEntries: ProcessedEntry[],
): Record<string, DocRegisterEntry[]> {
  const reg: Record<string, Record<string, DocRegisterEntry>> = {};
  processedEntries.forEach((p) => {
    const wsId =
      (p.R && p.R.catalogs && p.R.catalogs[0] && p.R.catalogs[0][0]) || "unassoc";
    const atts = (p.email.attachments || []) as SimAttachment[];
    if (!atts.length) return;
    const verdicts = docRegisterVerdicts(p.checks);
    if (!reg[wsId]) reg[wsId] = {};
    atts.forEach((a) => {
      const verdict = verdicts.get(a.filename);
      if (verdict?.classification === "noise") return;
      const key = _docKey(a);
      const existing = reg[wsId][key];
      const ver = verdict?.version ?? parseDocVersion(a.version);
      const v: DocRegisterVersion = {
        version: ver,
        filename: a.filename,
        source_email_id: p.email.id,
        date: (p.email.time || "").split(" · ")[0],
        /* Prefer the synced original (serverId) or an uploaded data URL; offline
           seed attachments carry only text content, so fall back to a data URL
           built from it rather than a dead /documents/ link. */
        fileData:
          a.fileData ||
          (a.content
            ? "data:text/plain;charset=utf-8," + encodeURIComponent(a.content)
            : undefined),
        serverId: a.serverId,
        is_current: verdict?.is_current,
        supersedes: verdict?.supersedes ?? null,
        authority_source: verdict?.authority_source,
      };
      if (!existing) {
        reg[wsId][key] = {
          doc_type: a.doc_type,
          name: _docStem(a.filename).replace(/_/g, " "),
          current_version: ver,
          versions: [v],
          ...(verdict?.authority_conflict
            ? {
                authority_conflict: true,
                authority_conflict_reason: verdict.authority_conflict_reason,
              }
            : {}),
        };
        return;
      }
      existing.versions.push(v);
      /* Currency is the agent's call. On an authority_conflict the register holds
         the prior current_version pending the lawyer's determination; when the
         agent marks a version current we honor that; otherwise (seed replays with
         no verdict) we fall back to highest-version. */
      if (verdict?.authority_conflict) {
        existing.authority_conflict = true;
        existing.authority_conflict_reason = verdict.authority_conflict_reason;
      } else if (verdict?.is_current === true) {
        existing.current_version = ver;
        existing.versions.forEach((e) => {
          e.is_current = e.version === ver;
        });
      } else if (verdict?.is_current === undefined) {
        existing.current_version = Math.max(existing.current_version, ver);
      }
    });
  });
  const flat: Record<string, DocRegisterEntry[]> = {};
  Object.keys(reg).forEach((wsId) => {
    flat[wsId] = Object.values(reg[wsId]);
  });
  return flat;
}

/* ----- participant resolution fallback ----- */
export interface RosterPerson {
  entityId: string;
  role: string;
}
/* address (lowercased) → entity/role, from the workstream entity rosters. */
export function buildAddressRoster(
  workstreams: Array<{
    entities?: Array<{ id: string; people?: Array<{ email: string; role: string }> }>;
  }>,
): Record<string, RosterPerson> {
  const m: Record<string, RosterPerson> = {};
  workstreams.forEach((w) =>
    (w.entities || []).forEach((en) =>
      (en.people || []).forEach((p) => {
        if (p.email) m[p.email.toLowerCase()] = { entityId: en.id, role: p.role };
      }),
    ),
  );
  return m;
}
/* Deterministic stand-in for a participant-check result when the run carries
   no structured output (seeded replays, restored snapshots, failed live
   checks): resolve the email's addresses against the known rosters. */
export function synthParticipantResult(
  email: SimEmail,
  roster: Record<string, RosterPerson>,
): { mapped: Array<{ email: string; entityId: string; role: string }>; unknown: string[] } | null {
  const addrs = [email.from, ...(email.to || []), ...(email.cc || [])]
    .map((a) => (a || "").toLowerCase())
    .filter(Boolean);
  const mapped: Array<{ email: string; entityId: string; role: string }> = [];
  const unknown: string[] = [];
  const seen = new Set<string>();
  addrs.forEach((a) => {
    if (seen.has(a)) return;
    seen.add(a);
    const hit = roster[a];
    if (hit) mapped.push({ email: a, entityId: hit.entityId, role: hit.role });
    else unknown.push(a);
  });
  return mapped.length || unknown.length ? { mapped, unknown } : null;
}

/* ----- SA doc-type mapping (canonical copy lives in sa.ts) ----- */
export { SA_DOC_TYPE, saDocType } from "./sa";

/* Re-export aliased helpers a few callers prefer to import from utils */
export { SimEmail };

/* ----- "ck" helper used by the simulation seeds ----- */
export const ck = (id: string, conf: number, out: string) => ({ id, conf, out });
